import React, { useState, useEffect } from 'react';

/**
 * SVGPreviewFromBlob component fetches an SVG file from a blob URL and displays it as a base64-encoded image.
 *
 * @param {Object} props - The component props.
 * @param {string} props.blobUrl - The blob URL of the SVG file to be fetched and displayed.
 * @returns {JSX.Element} The rendered component.
 */

type SVGPreviewFromBlobProps = {
  blobUrl: string;
};

const SVGPreviewFromBlob: React.FC<SVGPreviewFromBlobProps> = ({ blobUrl }) => {
  const [base64SVG, setBase64SVG] = useState<string | null>(null);

  useEffect(() => {
    const convertBlobToBase64 = async (blob: Blob): Promise<string | null> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = () => resolve(reader.result && (reader.result as string).split(',')[1]); // Extract base64 part
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    };

    const fetchAndConvertSVG = async () => {
      try {
        const response = await fetch(blobUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        const base64 = await convertBlobToBase64(blob);
        setBase64SVG(base64);
      } catch (error) {
        console.error('Error fetching or converting SVG:', error);
      }
    };

    fetchAndConvertSVG();
  }, [blobUrl]);

  if (base64SVG) {
    const svgDataURL = `data:image/svg+xml;base64,${base64SVG}`;
    return <img src={svgDataURL} alt="Preview of SVG from Blob" />;
  }

  return <p>Loading SVG...</p>;
};

export default SVGPreviewFromBlob;
